import React, { useState, useEffect } from "react";
import { Card, Form, Row, Col, Select, Button, Spin, message } from "antd";
import { getSamurthiBankDetailsData } from "./GeneralService";
import { generateGenderWiseReportExcel } from "../../Utils/GenderWiseReportExcel";
// import { generateSamurthiBankDetailsReport } from "./GeneralService";
// import { generateBlob } from "../../Utils/CreateBlob";

const { Option } = Select;
const ALL = "__ALL__";

const toErrorText = (err) => {
    if (!err) return "Report generation failed. Please try again.";
    if (typeof err === "string") return err;

    let status = err.status;
    let detail = "";

    let body = err.data;
    if (body instanceof ArrayBuffer || ArrayBuffer.isView(body)) {
        try {
            body = JSON.parse(new TextDecoder("utf-8").decode(body));
        } catch (e) {
            body = null;
        }
    }
    if (body && typeof body === "object") detail = body.message || body.error || "";

    if (detail) return status ? `${status} - ${detail}` : detail;
    if (status) return `Report generation failed (${status}).`;
    if (err.message) return String(err.message);
    return "Report generation failed. Please try again.";
};

const GenderWiseReport = ({
    loginPlaceId,
    districtData = [],
    districtName,
    divisionalId,
    branchId,
}) => {
    const [selectedDistricts,   setSelectedDistricts]   = useState([]);
    const [selectedDivisionals, setSelectedDivisionals] = useState([]);
    const [selectedBranches,    setSelectedBranches]    = useState([]);
    const [availableDivisionals, setAvailableDivisionals] = useState([]);
    const [availableBranches,    setAvailableBranches]    = useState([]);

    // const [isExcel,  setIsExcel]  = useState(false);
    const [loading,  setLoading]  = useState(false);

    useEffect(() => {
        if (loginPlaceId === "4" && districtName && districtData.length > 0) {
            const d = districtData.find((x) => x.districtName === districtName);
            if (d) setSelectedDistricts([d.districtId]);
        }
    }, [loginPlaceId, districtName, districtData]);

    useEffect(() => {
        if (loginPlaceId === "2" && divisionalId && districtData.length > 0) {
            const filtered = districtData
                .flatMap((d) => d.divisionalAreaDtoList || [])
                .filter((div) => div.divAId === divisionalId);
            setAvailableDivisionals(filtered);
            setSelectedDivisionals([divisionalId]);
        }
    }, [loginPlaceId, divisionalId, districtData]);

    useEffect(() => {
        if (loginPlaceId === "1" && branchId) {
            const allBranches = districtData
                .flatMap((d) => d.divisionalAreaDtoList || [])
                .flatMap((div) => div.branchDtoList || []);
            setAvailableBranches(allBranches);
            setSelectedBranches([branchId]);
        }
    }, [loginPlaceId, branchId, districtData]);

    useEffect(() => {
        if (loginPlaceId === "2" || loginPlaceId === "1") return;

        const filtered = districtData
            .filter((d) => selectedDistricts.includes(d.districtId))
            .flatMap((d) => d.divisionalAreaDtoList || []);

        setAvailableDivisionals(filtered);
        setSelectedDivisionals((prev) =>
            prev.filter((id) => filtered.some((div) => div.divAId === id))
        );
        setAvailableBranches([]);
        setSelectedBranches([]);
    }, [selectedDistricts, districtData, loginPlaceId]);

    useEffect(() => {
        if (loginPlaceId === "1") return;
        if (!selectedDivisionals.length) {
            setAvailableBranches([]);
            setSelectedBranches([]);
            return;
        }
        const filtered = availableDivisionals
            .filter((div) => selectedDivisionals.includes(div.divAId))
            .flatMap((div) => div.branchDtoList || []);

        setAvailableBranches(filtered);
        setSelectedBranches((prev) =>
            prev.filter((id) => filtered.some((b) => b.id === id))
        );
    }, [selectedDivisionals, availableDivisionals, loginPlaceId]);

    const isAllSelected = (selected, all) =>
        all.length > 0 && all.every((id) => selected.includes(id));

    const handleDistrictChange = (values) => {
        const allIds = districtData.map((d) => d.districtId);
        if (values.includes(ALL)) {
            setSelectedDistricts(isAllSelected(selectedDistricts, allIds) ? [] : allIds);
        } else {
            setSelectedDistricts(values);
        }
    };

    const handleDivisionalChange = (values) => {
        const allIds = availableDivisionals.map((d) => d.divAId);
        if (values.includes(ALL)) {
            setSelectedDivisionals(isAllSelected(selectedDivisionals, allIds) ? [] : allIds);
        } else {
            setSelectedDivisionals(values);
        }
        setSelectedBranches([]);
    };

    const handleBranchChange = (values) => {
        const allIds = availableBranches.map((b) => b.id);
        if (values.includes(ALL)) {
            setSelectedBranches(isAllSelected(selectedBranches, allIds) ? [] : allIds);
        } else {
            setSelectedBranches(values);
        }
    };

    const resolveDistrictLabel = () => {
        if (loginPlaceId === "4") return districtName || "All";
        if (!selectedDistricts.length) return "All";
        if (isAllSelected(selectedDistricts, districtData.map((d) => d.districtId))) return "All";
        return districtData
            .filter((d) => selectedDistricts.includes(d.districtId))
            .map((d) => d.districtName)
            .join(", ");
    };

    const resolveDivisionalLabel = () => {
        if (loginPlaceId === "2") {
            const div = availableDivisionals.find((d) => d.divAId === divisionalId);
            return div ? div.divAIdName : "All";
        }
        if (!selectedDivisionals.length) return "All";
        if (isAllSelected(selectedDivisionals, availableDivisionals.map((d) => d.divAId))) return "All";
        return availableDivisionals
            .filter((d) => selectedDivisionals.includes(d.divAId))
            .map((d) => d.divAIdName)
            .join(", ");
    };

    const resolveBranchLabel = () => {
        if (loginPlaceId === "1") {
            const b = availableBranches.find((x) => x.id === branchId);
            return b ? b.branchName : "Branch";
        }
        if (!selectedBranches.length) return "All";
        if (isAllSelected(selectedBranches, availableBranches.map((b) => b.id))) return "All";
        return availableBranches
            .filter((b) => selectedBranches.includes(b.id))
            .map((b) => b.branchName)
            .join(", ");
    };

    const buildPayload = () => {
        let effectiveBranchIds = null;
        if (loginPlaceId === "1") {
            effectiveBranchIds = branchId ? [branchId] : null;
        } else if (selectedBranches.length > 0 && !isAllSelected(selectedBranches, availableBranches.map((b) => b.id))) {
            effectiveBranchIds = selectedBranches;
        }

        let districtNameParam = null;
        if (loginPlaceId === "3") {
            if (selectedDistricts.length === 1) {
                const d = districtData.find((x) => x.districtId === selectedDistricts[0]);
                districtNameParam = d ? d.districtName : null;
            } else if (selectedDistricts.length > 1) {
                districtNameParam = "ALL";
            } else {
                districtNameParam = "ALL";
            }
        } else if (loginPlaceId === "4") {
            districtNameParam = districtName || null;
        }

        return {
            isExcel:      true,
            districtName: districtNameParam,
            branchIdList: effectiveBranchIds,
            divisionalIds: selectedDivisionals.length > 0 ? selectedDivisionals : null,
        };
    };

    const handleGenerate = async () => {
        setLoading(true);
        const payload = buildPayload();

        try {
            const responseData = await getSamurthiBankDetailsData(payload);
            const rawData = Array.isArray(responseData)
                ? responseData
                : (Array.isArray(responseData?.data) ? responseData.data : []);

            if (!rawData || rawData.length === 0) {
                message.warning("No data found for the selected parameters.");
                return;
            }

            const filters = {
                reportTitle: "Samurthi Bank Gender Wise Customer Details",
                district:    resolveDistrictLabel(),
                divisional:  resolveDivisionalLabel(),
                branches:    resolveBranchLabel(),
            };

            await generateGenderWiseReportExcel(rawData, filters, "GenderWiseReport");
            message.success("Excel report generated successfully!");
        } catch (err) {
            console.error("GenderWiseReport:", err);
            message.error(toErrorText(err), 6);
        } finally {
            setLoading(false);
        }
    };

    const districtAllSelected = isAllSelected(selectedDistricts, districtData.map((d) => d.districtId));
    const divisionalAllSelected = isAllSelected(selectedDivisionals, availableDivisionals.map((d) => d.divAId));
    const branchAllSelected = isAllSelected(selectedBranches, availableBranches.map((b) => b.id));

    return (
        <div className="panel-container">
            <Card hoverable className="inner-card">
                <Form layout="vertical">
                    <Row gutter={[16, 16]} justify="space-around">

                        {/* District – placeId 3 & 4 */}
                        {(loginPlaceId === "3" || loginPlaceId === "4") && (
                            <Col span={24}>
                                <Form.Item label="District">
                                    <Select
                                        mode="multiple"
                                        value={selectedDistricts}
                                        onChange={handleDistrictChange}
                                        disabled={loginPlaceId === "4"}
                                        allowClear
                                        showSearch
                                        maxTagCount="responsive"
                                        placeholder="Select District (All Districts by default)"
                                        filterOption={(input, option) =>
                                            option.children?.toString().toLowerCase().includes(input.toLowerCase())
                                        }
                                        style={{ width: "100%" }}
                                    >
                                        <Option value={ALL}>
                                            {districtAllSelected ? "❌ Clear All" : "✅ All Districts"}
                                        </Option>
                                        {districtData.map((d) => (
                                            <Option key={d.districtId} value={d.districtId}>
                                                🏙️ {d.districtName}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {/* Divisional – placeId 2, 3, 4 */}
                        {(loginPlaceId === "2" || loginPlaceId === "3" || loginPlaceId === "4") && (
                            <Col span={24}>
                                <Form.Item label="Divisional Area">
                                    <Select
                                        mode="multiple"
                                        value={selectedDivisionals}
                                        onChange={handleDivisionalChange}
                                        disabled={loginPlaceId === "2"}
                                        allowClear
                                        showSearch
                                        maxTagCount="responsive"
                                        placeholder="Select Divisional Area"
                                        filterOption={(input, option) =>
                                            option.children?.toString().toLowerCase().includes(input.toLowerCase())
                                        }
                                        style={{ width: "100%" }}
                                    >
                                        <Option value={ALL}>
                                            {divisionalAllSelected ? "❌ Clear All" : "✅ All Divisional Areas"}
                                        </Option>
                                        {availableDivisionals.map((d) => (
                                            <Option key={d.divAId} value={d.divAId}>
                                                🏘️ {d.divAIdName}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {/* Branch – all placeIds */}
                        <Col span={24}>
                            <Form.Item label="Branch">
                                <Select
                                    mode="multiple"
                                    value={selectedBranches}
                                    onChange={handleBranchChange}
                                    disabled={loginPlaceId === "1" || !availableBranches.length}
                                    allowClear
                                    showSearch
                                    maxTagCount="responsive"
                                    placeholder="Select Branch (All Branches by default)"
                                    filterOption={(input, option) =>
                                        option.children?.toString().toLowerCase().includes(input.toLowerCase())
                                    }
                                    style={{ width: "100%" }}
                                >
                                    <Option value={ALL}>
                                        {branchAllSelected ? "❌ Clear All" : "✅ All Branches"}
                                    </Option>
                                    {availableBranches.map((b) => (
                                        <Option key={b.id} value={b.id}>
                                            🏢 {b.branchName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        {/* Format toggle - Excel only */}
                        {/* <Col span={24}>
                            <Form.Item label="Generate Excel Sheet (Default: PDF)">
                                <Switch
                                    defaultChecked={false}
                                    checked={isExcel}
                                    onChange={setIsExcel}
                                    checkedChildren="Excel"
                                    unCheckedChildren="PDF"
                                />
                            </Form.Item>
                        </Col> */}

                        {/* Generate button */}
                        <Col span={24} style={{ textAlign: "right" }}>
                            <Form.Item className="generate-btn-outline">
                                <Button
                                    className="generate-btn"
                                    onClick={handleGenerate}
                                    disabled={loading}
                                >
                                    {loading ? <Spin /> : "Generate Report"}
                                </Button>
                            </Form.Item>
                        </Col>

                    </Row>
                </Form>
            </Card>
        </div>
    );
};

export default GenderWiseReport;
